import React, { useState } from 'react';
import { PublicHTTPService } from '../../../services/publicHttpService';
import JobviousLogo from "./../../../assets/Jobvious_logo@2x.svg";

const API = new PublicHTTPService();

const PublicReferralForm = () => {
  const [step, setStep] = useState('form');
  const [formData, setFormData] = useState({
    referring_email: '',
    customer_name: '',
    customer_email: '',
    vin: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for VIN - convert to uppercase
    const processedValue = name === 'vin' ? value.toUpperCase() : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.referring_email.trim()) {
      newErrors.referring_email = 'Your email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.referring_email)) {
      newErrors.referring_email = 'Please enter a valid email address';
    }
    
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }
    
    if (!formData.customer_email.trim()) {
      newErrors.customer_email = 'Customer email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      newErrors.customer_email = 'Please enter a valid customer email address';
    }
    
    if (!formData.vin.trim()) {
      newErrors.vin = 'VIN is required';
    } else if (!/^[A-HJ-NPR-Z0-9]{17}$/i.test(formData.vin)) {
      newErrors.vin = 'VIN must be exactly 17 characters (letters and numbers, excluding I, O, Q)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await API.post('/public/referral', formData);
      
      if (response.data.success) {
        setSubmittedData({
          referring_email: formData.referring_email,
          customer_name: formData.customer_name,
          referralId: response.data.referralId
        });
        setStep('success');
      } else {
        setErrors({ submit: response.data.error || 'Failed to submit referral. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting referral:', error);
      setErrors({ submit: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (step === 'form') {
    return (
      <div className="min-vh-100 bg-light">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="container py-4">
            <div className="d-flex align-items-center">
              <img
                src={JobviousLogo}
                alt="Jobvious Logo"
                className="img-fluid"
                style={{ maxWidth: "200px", height: "auto" }}
              />
              <small className="text-muted ms-3">
                Bringing Car Dealers & Specialist Installers together
              </small>
            </div>
          </div>
        </div>

        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card shadow">
                <div className="card-body p-5">
                  {/* Header */}
                  <div className="text-center mb-5">
                    <div className="text-warning mb-3">
                      <i className="fas fa-handshake fa-3x"></i>
                    </div>
                    <h1 className="h2 mb-3">Submit Customer Referral</h1>
                    <p className="text-muted">
                      Help connect interested customers with automotive specialists in your area
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit}>
                    {errors.submit && (
                      <div className="alert alert-danger">
                        {errors.submit}
                      </div>
                    )}

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Your Email *</label>
                        <input
                          type="email"
                          name="referring_email"
                          value={formData.referring_email}
                          onChange={handleInputChange}
                          className={`form-control ${errors.referring_email ? 'is-invalid' : ''}`}
                          placeholder="your.email@dealer.com"
                        />
                        {errors.referring_email && (
                          <div className="invalid-feedback">{errors.referring_email}</div>
                        )}
                        <small className="text-muted">We'll use this to track your referral</small>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Customer Name *</label>
                        <input
                          type="text"
                          name="customer_name"
                          value={formData.customer_name}
                          onChange={handleInputChange}
                          className={`form-control ${errors.customer_name ? 'is-invalid' : ''}`}
                          placeholder="John Smith"
                        />
                        {errors.customer_name && (
                          <div className="invalid-feedback">{errors.customer_name}</div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Customer Email *</label>
                        <input
                          type="email"
                          name="customer_email"
                          value={formData.customer_email}
                          onChange={handleInputChange}
                          className={`form-control ${errors.customer_email ? 'is-invalid' : ''}`}
                          placeholder="customer@email.com"
                        />
                        {errors.customer_email && (
                          <div className="invalid-feedback">{errors.customer_email}</div>
                        )}
                        <small className="text-muted">Specialist will contact them here</small>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Vehicle VIN *</label>
                        <input
                          type="text"
                          name="vin"
                          value={formData.vin}
                          onChange={handleInputChange}
                          className={`form-control ${errors.vin ? 'is-invalid' : ''}`}
                          placeholder="1HGCM82633A123456"
                          maxLength="17"
                          style={{ fontFamily: 'monospace' }}
                        />
                        {errors.vin && (
                          <div className="invalid-feedback">{errors.vin}</div>
                        )}
                        <small className="text-muted">17-character Vehicle Identification Number</small>
                      </div>
                    </div>

                    <div className="mt-4">
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-lg w-100"
                          style={{ backgroundColor: '#FF9516', borderColor: '#FF9516', color: 'white' }}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              Submitting Referral...
                            </>
                          ) : (
                            'Submit Customer Referral'
                          )}
                        </button>
                    </div>

                    <div className="mt-3 text-center">
                      <small className="text-muted">
                        By submitting this referral, you confirm the customer has expressed interest in automotive services.
                      </small>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="min-vh-100 bg-light">
        {/* Header */}
        <div className="bg-white shadow-sm">
          <div className="container py-4">
            <div className="d-flex align-items-center">
              <img
                src={JobviousLogo}
                alt="Jobvious Logo"
                className="img-fluid"
                style={{ maxWidth: "200px", height: "auto" }}
              />
              <small className="text-muted ms-3">
                Bringing Car Dealers & Specialist Installers together
              </small>
            </div>
          </div>
        </div>

        <div className="container py-5">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card shadow">
                <div className="card-body p-5">
                  {/* Success Message */}
                  <div className="text-center mb-5">
                    <div className="text-success mb-3">
                      <i className="fas fa-check-circle fa-3x"></i>
                    </div>
                    <h1 className="h2 mb-3">Referral Submitted Successfully!</h1>
                    <p className="text-muted">
                      Your referral has been added to our system and will be matched with qualified specialists.
                    </p>
                  </div>

                  {/* Referral Details */}
                  <div className="alert alert-info">
                    <h4 className="alert-heading mb-3">Referral Details</h4>
                    <div className="row">
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Referral ID:</small>
                        <strong>#{submittedData?.referralId}</strong>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Customer:</small>
                        <strong>{submittedData?.customer_name}</strong>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Referred by:</small>
                        <strong>{submittedData?.referring_email}</strong>
                      </div>
                      <div className="col-md-6 mb-2">
                        <small className="text-muted d-block">Status:</small>
                        <span className="badge bg-success">Active</span>
                      </div>
                    </div>
                  </div>

                  {/* What Happens Next */}
                  <div className="alert alert-warning">
                    <h4 className="alert-heading mb-3">What Happens Next?</h4>
                    <ol className="mb-0">
                      <li><strong>Specialist Matching</strong> - We'll find qualified specialists in your area</li>
                      <li><strong>Customer Contact</strong> - A specialist will reach out to the customer within 24-48 hours</li>
                      <li><strong>Service Delivery</strong> - The specialist provides quality service to your customer</li>
                      <li><strong>Commission Payment</strong> - You receive commission when the job is completed</li>
                    </ol>
                  </div>

                  {/* Actions */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <button
                        onClick={() => {
                          setStep('form');
                          setFormData({
                            referring_email: formData.referring_email, // Keep email for convenience
                            customer_name: '',
                            customer_email: '',
                            vin: ''
                          });
                          setErrors({});
                          setSubmittedData(null);
                        }}
                          className="btn btn-lg w-100"
                          style={{ backgroundColor: '#FF9516', borderColor: '#FF9516', color: 'white' }}
                      >
                        Submit Another Referral
                      </button>
                    </div>
                    <div className="col-md-6 mb-3">
                      <a
                        href="https://jobvious.com"
                        className="btn btn-lg w-100"
                        style={{ backgroundColor: '#FF9516', borderColor: '#FF9516', color: 'white' }}
                      >
                        Learn More About Jobvious
                      </a>
                    </div>
                  </div>

                  {/* Support */}
                  <div className="border-top pt-4 text-center">
                    <small className="text-muted">
                      Questions about your referral? Contact us at{' '}
                      <a href="mailto:support@jobvious.com" className="text-decoration-none">
                        support@jobvious.com
                      </a>
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PublicReferralForm;